import React, {
  HTMLAttributes,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { makeStyles } from "tss-react/mui";
import { Box, Checkbox } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";

import { FilterSelectMultiple, Item, Option, OptionList } from "../interfaces";
import { GenericObject } from "../../services/Price";
import { useDebounce } from "../../hooks/debounce";
import { getSelectFilterValues } from "../utils";
import { SCROLL_BAR_STYLES_THIN } from "../../Theme";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { SELECT_ALL_ID } from "../../hooks/useSelectAllOptions";
import ColorSwatch from "../../components/ColorSwatch";
import { SX_CONTROLLED_AUTOCOMPLETE_SIZE } from "../styles";

const useStyles = makeStyles()(() => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    width: "270px",
  },
  scrollbar: SCROLL_BAR_STYLES_THIN,
}));

const useGetWatchLookupValue = (
  filters: GenericObject,
  filter: FilterSelectMultiple
) => (filter.watchLookup ? filters[filter.watchLookup] : false);

export const createRenderOption = (filter: FilterSelectMultiple) => {
  if (filter.renderOption && filter.renderOption.type === "color") {
    const renderOption = filter.renderOption;

    return (
      props: HTMLAttributes<HTMLLIElement>,
      option: Option,
      { selected }: AutocompleteRenderOptionState
    ) => (
      <Box
        component="li"
        {...props}
        key={option.id}
        sx={{ fontSize: 14, "&.MuiAutocomplete-option": { padding: 0 } }}
      >
        <Checkbox
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          size="small"
          checked={selected}
        />
        {option[renderOption.key] && (
          <ColorSwatch color={option[renderOption.key]} sx={{ mr: 1 }} />
        )}
        {option.label}
      </Box>
    );
  }

  return (
    props: HTMLAttributes<HTMLLIElement>,
    option: Option,
    { selected }: AutocompleteRenderOptionState
  ) => (
    <Box
      component="li"
      {...props}
      key={option.id}
      sx={{ fontSize: 14, "&.MuiAutocomplete-option": { padding: 0 } }}
    >
      <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        size="small"
        checked={selected}
      />

      {option.label}
    </Box>
  );
};

/**
 * SelectMultipleFilter component.
 */
export const SelectMultipleFilter = memo(
  ({
    config,
    filters,
    filter,
    filterValue,
    onChange,
    updateFilterOptions,
    limitTags = 0,
    mustTranslateOptions = false,
  }: {
    config: {
      api: string;
      apiParams?: GenericObject;
      label: string;
      labelKey: string;
      type: string;
    };
    filterValue: Option[] | null;
    filters: GenericObject;
    filter: FilterSelectMultiple;
    onChange: (newValue: any) => void;
    updateFilterOptions?: (options: Option) => void;
    mustTranslateOptions: boolean;
    limitTags?: number;
  }) => {
    const intl = useIntl();
    const { classes: basicsClasses } = useStyles();
    const [options, setOptions] = useState<OptionList>([]);
    const [initialValues, setInitialValues] = useState<string | null>();
    const [newValues, setNewValues] = useState<Option[]>(filterValue ?? []);
    const [configParams, setConfigParams] = useState<GenericObject | undefined>(
      config.apiParams
    );

    const lookupValue: string | false = useGetWatchLookupValue(filters, filter);
    const debounceSearchValue = useDebounce(initialValues, 400);
    const prevLookupValue = useRef(lookupValue);

    const query = useQuery(
      [config.api, configParams, debounceSearchValue],
      () =>
        getSelectFilterValues<Item>(
          config.api,
          configParams,
          debounceSearchValue
        )
    );

    const renderOption = useMemo(() => createRenderOption(filter), [filter]);

    useEffect(() => {
      if (filter.watchLookup && lookupValue) {
        setConfigParams((prev) => {
          return {
            ...prev,
            property: lookupValue,
          };
        });
      }
    }, [lookupValue, filter.watchLookup]);

    useEffect(() => {
      if (prevLookupValue.current === lookupValue) return;
      prevLookupValue.current = lookupValue;
      if (filter.watchLookup) {
        onChange([]);
        setNewValues([]);
      }
    }, [onChange, filter.watchLookup, lookupValue]);

    useEffect(() => {
      if (query.data) {
        const options = query.data.results.map((option: Item) => {
          if (filter.renderOption) {
            return {
              id: option.id,
              [filter.renderOption.key]: option[filter.renderOption.key],
              label: mustTranslateOptions
                ? intl.formatMessage({ id: option[config.labelKey] })
                : option[config.labelKey],
            };
          }

          return {
            id: option.id,
            label: mustTranslateOptions
              ? intl.formatMessage({ id: option[config.labelKey] })
              : option[config.labelKey],
          };
        });

        setOptions(options);
      }
    }, [
      intl,
      mustTranslateOptions,
      query.data,
      config.labelKey,
      filter.renderOption,
    ]);

    useEffect(() => {
      if (filter.usefulForRankability && options[0] && updateFilterOptions) {
        updateFilterOptions(options[0]);
      }
    }, [options, updateFilterOptions, filter.usefulForRankability]);

    // Reconcile blank-label placeholders (from URL restore) with loaded options
    useEffect(() => {
      if (options.length === 0) return;
      setNewValues((prev) => {
        if (
          !prev.some(
            (value) => typeof value !== "object" || !(value as Option).label
          )
        )
          return prev;
        return prev.map((value) => {
          if (typeof value === "object" && (value as Option).label)
            return value as Option;
          const id = typeof value === "object" ? (value as Option).id : value;
          return options.find((o) => o.id === id) ?? value;
        });
      });
    }, [options]);

    // Reset filter on reset button filter click
    useEffect(() => {
      if (filterValue === null) {
        setNewValues([]);
      }
    }, [filterValue]);

    const onChangeHandler = (values: Option[]): void => {
      setNewValues(values);
      onChange(values);
    };

    return (
      <Box className={basicsClasses.field}>
        <CustomAutocomplete
          multiple
          limitTags={
            limitTags && !filter.controlledInputSize
              ? limitTags
              : filter.controlledInputSize
              ? 1
              : undefined
          }
          disableCloseOnSelect
          disabled={!!filter.watchLookup && !lookupValue}
          getOptionLabel={(option) => {
            return option.label || "";
          }}
          isOptionEqualToValue={(option: Option, value: Option) =>
            String(option.id) === String(value.id)
          }
          loading={query.isLoading}
          options={options}
          onChange={(_event, values) => onChangeHandler(values)}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason !== "reset") {
              setInitialValues(newInputValue);
            }
          }}
          placeholder={intl.formatMessage({ id: config.label })}
          renderOptions={renderOption}
          // Same row as the options, so the "select all" rows line up with them
          renderSelectAllOption={(props, { label, selected }) =>
            renderOption(props, { id: SELECT_ALL_ID, label } as Option, {
              selected,
              index: -1,
              inputValue: "",
            })
          }
          size="small"
          variant="standard"
          sx={{
            ...(filter.controlledInputSize
              ? SX_CONTROLLED_AUTOCOMPLETE_SIZE
              : {}),
            "& .MuiFormControl-root .MuiInputBase-root": { padding: 0 },
            "& .MuiAutocomplete-inputRoot": {
              height: "unset",
            },
          }}
          value={newValues}
        />
      </Box>
    );
  }
);
